import type { AssignedQuizFilters } from '@repo/types/pagination';

import { useGetAssignedQuizzesInfinite } from '@repo/api/quiz/useGetAssignedQuizzesInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { useState } from 'react';

interface UseAssignedQuizzesData {
  enabledAssignedQuizzes?: boolean;
  groupId?: number;
}

export function useAssignedQuizzesData(useAssignedQuizzesData?: UseAssignedQuizzesData) {
  const { enabledAssignedQuizzes = true, groupId } = useAssignedQuizzesData || {};
  const [startDialog, setStartDialog] = useState<[boolean, string | null]>([false, null]);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const searchDebouncedValue: string | undefined = useDebounceValue(searchValue, 400);
  const [filterValue, setFilterValue] = useState<AssignedQuizFilters | undefined>(undefined);

  const getAssignedQuizzes = useGetAssignedQuizzesInfinite({
    name: searchDebouncedValue,
    filter: filterValue,
    groupId,
    enabled: enabledAssignedQuizzes,
  });

  const { data, isEmpty, noResults } = useInfiniteQueryResult(getAssignedQuizzes.data, searchDebouncedValue);

  const next = async () => {
    await getAssignedQuizzes.fetchNextPage();
  };

  const startQuiz = async (quizId: string) => {
    setStartDialog([true, quizId]);
  };

  return {
    state: {
      searchValue,
      filterValue,
      startDialog,
      data,
      isEmpty,
      noResults,
      hasNextPage: getAssignedQuizzes.hasNextPage,
      isFetching: getAssignedQuizzes.isFetching,
      isError: getAssignedQuizzes.isError,
      isLoading: getAssignedQuizzes.isLoading,
      isPending: getAssignedQuizzes.isPending,
    },
    actions: {
      next,
      setSearchValue,
      setFilterValue,
      setStartDialog,
      startQuiz,
    },
  };
}
